import { inject, Injectable, Injector } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';

// rxjs
import { catchError, Observable, shareReplay, take, throwError } from 'rxjs';

// services
import { TuiNotificationService } from '@taiga-ui/core';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { SKIP_GLOBAL_HTTP_ERROR_ALERT } from './http-error-context.token';

// sentry
import * as Sentry from '@sentry/angular';
import {
  isAnonymousAuthBootstrapRequestUrl,
  isGuestRestrictedRequestApiUrl,
} from '@core/helpers';
import { AccountService } from '@core/services/account/account.service';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private readonly alertService = inject(TuiNotificationService);
  private readonly accountService = inject(AccountService);
  private readonly injector = inject(Injector);
  private translateService: OutkeepTranslateService | null = null;

  // urls that should not show alerts messages
  private readonly exceptionUrls = new Set([
    '/api2/logistic/required-fields/validate',
    '/api2/logistic/request-counterparties',
  ]);

  public intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status !== 401) {
          Sentry.captureException(err);
        }
        if (
          this.shouldSkipUserAlert(request, err) ||
          request.context.get(SKIP_GLOBAL_HTTP_ERROR_ALERT)
        ) {
          return throwError(() => err);
        }
        if (err.status === 409 && this.isLtmRequest(request)) {
          return throwError(() => err);
        }
        switch (err.status) {
          // TODO add more cases ?
          default:
            this.showUserFriendlyError(err);
            break;
        }

        return throwError(() => err);
      }),
      shareReplay(1),
    );
  }

  private shouldSkipUserAlert(
    request: HttpRequest<unknown>,
    err: HttpErrorResponse,
  ): boolean {
    // 401 для анонима ожидаем во время deep-link login flow:
    // внешний auth уже открыт поверх страницы, поэтому toast здесь только шумит.
    return (
      this.exceptionUrls.has(request.url) ||
      this.isGuestRestrictedRequestNotFound(request, err) ||
      (err.status === 401 &&
        (this.accountService.$authState() === 'anonymous' ||
          isAnonymousAuthBootstrapRequestUrl(request.urlWithParams)))
    );
  }

  private isLtmRequest(request: HttpRequest<unknown>): boolean {
    return request.url.includes('/api2/logistic/time-management/');
  }

  private isGuestRestrictedRequestNotFound(
    request: HttpRequest<unknown>,
    err: HttpErrorResponse,
  ): boolean {
    return (
      err.status === 404 &&
      request.method === 'GET' &&
      isGuestRestrictedRequestApiUrl(request.urlWithParams)
    );
  }

  private showUserFriendlyError(err: HttpErrorResponse): void {
    let title = this.t('core.interceptors.http_error.default_title', 'Ошибка');
    let message = `<code><b>${this.t('core.interceptors.http_error.status_label', 'Статус')}: ${err.status}</b></code><br/>`;

    switch (err.status) {
      case 0:
        title = this.t(
          'core.interceptors.http_error.no_connection_title',
          'Нет соединения',
        );
        message += this.t(
          'core.interceptors.http_error.no_connection_message',
          'Проверьте подключение к интернету',
        );
        break;
      case 400:
        title = this.t(
          'core.interceptors.http_error.bad_request_title',
          'Неверный запрос',
        );
        message +=
          this.handleErrorMessage(err) ||
          this.t(
            'core.interceptors.http_error.bad_request_message',
            'Проверьте введенные данные',
          );
        break;
      case 403:
        title = this.t(
          'core.interceptors.http_error.forbidden_title',
          'Доступ запрещен',
        );
        message += this.t(
          'core.interceptors.http_error.forbidden_message',
          'У вас нет прав для выполнения этой операции',
        );
        break;
      case 404:
        title = this.t(
          'core.interceptors.http_error.not_found_title',
          'Не найдено',
        );
        message += this.t(
          'core.interceptors.http_error.not_found_message',
          'Запрашиваемый ресурс не найден',
        );
        break;
      case 500:
        title = this.t(
          'core.interceptors.http_error.server_error_title',
          'Ошибка сервера',
        );
        message += this.t(
          'core.interceptors.http_error.server_error_message',
          'Внутренняя ошибка сервера. Попробуйте позже',
        );
        break;
      case 502:
        title = this.t(
          'core.interceptors.http_error.bad_gateway_title',
          'Плохой шлюз',
        );
        message += this.t(
          'core.interceptors.http_error.bad_gateway_message',
          'Сервер получил недопустимый ответ от вышестоящего сервера',
        );
        break;
      case 503:
        title = this.t(
          'core.interceptors.http_error.service_unavailable_title',
          'Сервис недоступен',
        );
        message += this.t(
          'core.interceptors.http_error.service_unavailable_message',
          'Сервер временно недоступен. Попробуйте позже',
        );
        break;
      default:
        message += this.handleErrorMessage(err);
        break;
    }

    this.alertService
      .open(message, {
        label: title,
        appearance: 'negative',
      })
      .pipe(take(1))
      .subscribe();
  }

  private handleErrorMessage(err: HttpErrorResponse): string {
    if (!err.error) {
      return (
        err.message ||
        this.t('core.interceptors.http_error.unknown_error', 'Unknown error')
      );
    }
    if (typeof err.error === 'string') {
      return err.error;
    }
    if (typeof err.error === 'object') {
      return (
        err.error.data ||
        err.error.message ||
        this.t('core.interceptors.http_error.unknown_error', 'Unknown error')
      );
    }

    return (
      err.message ||
      this.t(
        'core.interceptors.http_error.unexpected_error',
        'Произошла непредвиденная ошибка',
      )
    );
  }

  private t(key: string, fallback: string): string {
    const translated = this.getTranslateService()?.instant(key);

    return translated && translated !== key ? translated : fallback;
  }

  private getTranslateService(): OutkeepTranslateService | null {
    if (this.translateService) {
      return this.translateService;
    }

    try {
      this.translateService = this.injector.get(OutkeepTranslateService);

      return this.translateService;
    } catch {
      return null;
    }
  }
}
